import { IntegrationKey, IntegrationStatus } from '@ankor-io/common/types/integrations/types'
import { Badge, Card, Loading } from '@ankor-io/ui-components'

import QuickBooksLogoSrc from '@/assets/Quickbooks.svg'
import XeroLogoSrc from '@/assets/Xero.svg'
import { useOrganizationIntegrationsQuery } from '@/queries/organization'

type IntegrationUiConfig = {
  description: string
  logo: string
}

const INTEGRATION_UI_CONFIG: Partial<Record<IntegrationKey, IntegrationUiConfig>> = {
  [IntegrationKey.XERO_EXPENSES]: {
    description: 'Integrate your expenses and receipts to Xero',
    logo: XeroLogoSrc,
  },
  [IntegrationKey.QUICKBOOKS]: {
    description: 'Integrate your expenses and receipts to QuickBooks',
    logo: QuickBooksLogoSrc,
  },
}

export function IntegrationCardsView({
  organizationId,
  onSelect,
}: {
  organizationId: string
  onSelect: (integrationKey: IntegrationKey) => void
}) {
  const {
    data: integrations,
    isPending: isListPending,
    error: listError,
  } = useOrganizationIntegrationsQuery(organizationId)

  if (isListPending) return <Loading variant="spinner" />
  if (listError) return <div className="text-sm text-red-500">Error: {listError.message}</div>
  if (!integrations || integrations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <div className="text-4xl">🔗</div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">No integrations available</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Enable integrations for this organization via feature toggles to see them here.
        </p>
      </div>
    )
  }

  const integrationsWithConfig = integrations.filter((i) => INTEGRATION_UI_CONFIG[i.key])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {integrationsWithConfig.map((integration) => {
        const config = INTEGRATION_UI_CONFIG[integration.key]
        if (!config) return null

        const isActive = integration.status === IntegrationStatus.ACTIVE

        return (
          <div
            key={integration.key}
            onClick={() => onSelect(integration.key)}
          >
            <Card
              key={integration.key}
              imageSrc={config.logo}
              imageLayout="object-contain"
              layout="vertical"
              title={integration.name}
              description={config.description}
              badge={isActive ? <Badge color="emerald">Active</Badge> : <Badge color="gray">Inactive</Badge>}
            />
          </div>
        )
      })}
    </div>
  )
}
